import { useStations } from '../../predictionMap/hooks/useStations';
import { useState, useMemo, useEffect } from 'react';
import PredictionMap from '../../predictionMap/components/PredictionMap';
import useGetLocation from '../../../hooks/useGetLocation';
import useGetAlert from '../hooks/useGetAlertsByClient';
import { Stack, Box, Button, Typography, FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import { useIsMobile } from '../../../hooks/useIsMobile';
import usePaymentModal from '../../../features/predictionMap/hooks/usePaymentModal';
import { useTranslation } from 'react-i18next';
import { store } from '../../../store';
import { userModelType } from 'features/auth/types';
import { useOtherSettings } from './useOtherSettings';
import { alertResponseType } from '../types';
import { getAlertByAlertID, postAlert, putAlertById } from '../requests';
import { useLoaderData, useLocation, useNavigate } from 'react-router-dom';
import AlertDetailSetter from './AlertDetailSetter';
import useFetchUser from '../../../features/client/hooks/useFetchUser';
import { authType } from '../../../features/client/types';
import { getRivfldDepth, Station } from 'features/predictionMap/api/getStations';
import { sendAnalyticsEvent } from '../../../utils/googleAnalytics';

export const AddAlertByRiverLevelTab = () => {
  const isMobile = useIsMobile();
    // GA4: 河川水位アラートの河川選択を計測
  const { t, i18n } = useTranslation();
  const { togglePaymentModal, PaymentModal } = usePaymentModal('alert');
  const { OtherSettingsComponents, otherSettings, setOtherSettings } = useOtherSettings();
  const location = useLocation();
  const navigate = useNavigate();
  const is_detail = new URLSearchParams(location.search).get('is_detail');
  const authInfo = useLoaderData() as authType;
  const { user, isPaid } = useFetchUser(authInfo.user.cognito_username);

  const { stations } = useStations();
  const [selectedRiver, setSelectedRiver] = useState('');
  const [selectedPoint, setSelectedPoint] = useState('');

  // PredictionMap用のダミーprops取得
  const alerts = useGetAlert();
  const currentPosition = useGetLocation();
  const goToCurrentLocation = () => {};

  // 選択中の河川・地点のデータ取得
  const selectedRiverData = useMemo(() => {
    if (!selectedRiver) return null;
    // basin_nameが一致するstation群
    return stations.filter(st => st.basin_name === selectedRiver);
  }, [stations, selectedRiver]);

  const selectedPointData = useMemo(() => {
    if (!selectedPoint) return null;
    // idが一致するstation
    return stations.find(st => String(st.id) === selectedPoint) || null;
  }, [stations, selectedPoint]);

  // basin_nameでユニークな河川リストを作成
  const riverList = useMemo(() => {
    const set = new Set<string>();
    stations.forEach(st => {
      if (st.basin_name) set.add(st.basin_name);
    });
    return Array.from(set);
  }, [stations]);

  // 選択した河川の地点リスト（river_name / station_name）
  const pointList = useMemo(() => {
    if (!selectedRiver) return [];
    return stations
      .filter(st => st.basin_name === selectedRiver)
      .map(st => ({
        id: String(st.id),
        label: `${st.station_name}`,
      }));
  }, [stations, selectedRiver]);

  // only called when first rendering
  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const id = queryParams.get('id');

    if (!!id) {
      getAlertByAlertID(id)
        .then(data => {
          if (!data || !data.address || !data?.station) {
            throw new Error('');
          }
          setSelectedRiver(data.address);
          if (data?.station) setSelectedPoint(`${data.station}`);
        })
        .catch(d => {
        });
    }
  }, [location]);

  const handleRiverChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    sendAnalyticsEvent('alert_form', {
      action: 'select_river',
      alert_type: 'river_level',
    });
    setSelectedRiver(e.target.value);
    setSelectedPoint('');
  };
  const handlePointChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    // GA4: 河川水位アラートの観測所選択を計測
    sendAnalyticsEvent('alert_form', {
      action: 'select_station',
      alert_type: 'river_level',
      station_id: e.target.value,
    });
    setSelectedPoint(e.target.value);
  };

  const handleNextClick = async () => {
    if (!selectedPointData) {
      alert(t('pages.alerts.invalidLatLon'));
      return;
    }
    const user = (await store.getItem('user')) as userModelType;
    const data = {
      name: selectedPointData.station_name,
      longitude: selectedPointData.lon,
      latitude: selectedPointData.lat,
      address: selectedPointData.basin_name,
      is_area: false,
      station_id: selectedPointData.id,
      original_user: (!otherSettings.shareWithClient && user.id) || null,
    }
 
    const queryParams = new URLSearchParams(location.search);
    const id = queryParams.get('id');
    let res = false as alertResponseType | boolean;
    // idがあったらput
    if (!!id) {
      res = await putAlertById(id, data);
    } else {
      res = await postAlert(data);
    }
    if (!!res && !!res) {
      // GA4: 河川水位アラートの登録・更新成功を計測
      sendAnalyticsEvent('alert_form', {
        action: !!id ? 'update_success' : 'create_success',
        alert_type: 'river_level',
        station_id: selectedPointData.id,
      });
      navigate(`/prediction/new_alert?id=${res.id}&is_detail=true&is_river=true`);
    }
    return;
  };

  const handleSetRiverAndPoint = async (index: number) => {
      const station: Station = stations[index];

      // GA4: 地図上の観測所から河川水位アラート対象を選択した操作を計測
      sendAnalyticsEvent('alert_form', {
        action: 'select_station_from_map',
        alert_type: 'river_level',
        station_id: station.id,
      });

      setSelectedRiver(station.basin_name);
      setSelectedPoint(`${station.id}`);
    }

  const canSend = !!selectedPointData

  if (is_detail) {
    return (
      <>
        <AlertDetailSetter />
      </>
    );
  }

  return (
    <Stack direction={isMobile ? 'column' : 'row'}>
      {PaymentModal}

      <Box sx={{ width: isMobile ? '100%' : '45%' }}>
        <Typography style={{ fontWeight: 'bold', fontSize: '12pt' }}>
          1. {t('pages.alerts.addAlertByRiverLevelTab.enterRiverLevel', '河川から指定')}
        </Typography>

        <Stack direction="row" spacing={2} mt={2}>
          <FormControl variant="outlined" size="small" sx={{ minWidth: 180 }}>
            <InputLabel id="river-select-label">河川を選択</InputLabel>
            <Select
              labelId="river-select-label"
              value={selectedRiver}
              onChange={(e) => handleRiverChange(e as any)}
              label="河川を選択"
            >
              <MenuItem value=""><em>河川を選択</em></MenuItem>
              {riverList.map(river => (
                <MenuItem key={river} value={river}>{river}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl variant="outlined" size="small" sx={{ minWidth: 220 }}>
            <InputLabel id="point-select-label">地点を選択</InputLabel>
            <Select
              labelId="point-select-label"
              value={selectedPoint}
              onChange={(e) => handlePointChange(e as any)}
              label="地点を選択"
              disabled={!selectedRiver}
            >
              <MenuItem value=""><em>地点を選択</em></MenuItem>
              {pointList.map(point => (
                <MenuItem key={point.id} value={point.id}>{point.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>

        <OtherSettingsComponents
          otherSettings={otherSettings}
          setOtherSettings={setOtherSettings}
        />

        {/* 次へボタン */}
        <Box style={{ marginTop: '36px' }}>
          <Button
            variant="contained"
            style={canSend ? { backgroundColor: '#007B43' } : {}}
            onClick={handleNextClick}
            disabled={!(isPaid && canSend)}
          >
            {t('pages.alerts.goNext')}
          </Button>
        </Box>
      </Box>

      {/* PredictionMapを表示（isMobile時は上に表示） */}
      <Box
        sx={{
          width: isMobile ? '100%' : '50%',
          ...(isMobile ? {
            order: -1,
            mb: 2,
            height: 300,
            minHeight: 0,
            maxHeight: 300,
            overflow: 'hidden',
          } : { marginLeft: '5%' }),
        }}
      >
        <PredictionMap
          latlng={currentPosition}
          alerts={alerts}
          goToCurrentLocation={goToCurrentLocation}
          pins={stations}
          onClick={handleSetRiverAndPoint}
          style={{ height: '55vh' }}
          setIncrement={() => {}}
          increment={0}
          isUrlLoading={false}
          isImageIdChecking={false}
          showFloodDepthControls={false}
        />
      </Box>
    </Stack>
  );
};
